// ================ Action types ================ //

import { contactSupport } from '../util/api';
import { denormalisedResponseEntities } from '../util/data';

export const CONTACT_MODAL_OPEN = 'app/contact/CONTACT_MODAL_OPEN';

export const SEND_CONTACT_FORM_REQUEST = 'app/contact/SEND_CONTACT_FORM_REQUEST';
export const SEND_CONTACT_FORM_SUCCESS = 'app/contact/SEND_CONTACT_FORM_SUCCESS';
export const SEND_CONTACT_FORM_ERROR = 'app/contact/SEND_CONTACT_FORM_ERROR';
export const RESET_CONTACT_FORM = 'app/contact/RESET_CONTACT_FORM';

export const FETCH_CONTACT_BOOKINGS_REQUEST = 'app/contact/FETCH_CONTACT_BOOKINGS_REQUEST';
export const FETCH_CONTACT_BOOKINGS_SUCCESS = 'app/contact/FETCH_CONTACT_BOOKINGS_SUCCESS';
export const FETCH_CONTACT_BOOKINGS_ERROR = 'app/contact/FETCH_CONTACT_BOOKINGS_ERROR';

export const FETCH_CONTACT_LISTINGS_REQUEST = 'app/contact/FETCH_CONTACT_LISTINGS_REQUEST';
export const FETCH_CONTACT_LISTINGS_SUCCESS = 'app/contact/FETCH_CONTACT_LISTINGS_SUCCESS';
export const FETCH_CONTACT_LISTINGS_ERROR = 'app/contact/FETCH_CONTACT_LISTINGS_ERROR';

// ================ Reducer ================ //

/** @type {import('../../types/vansite').VanSite.State.Contact} */
const initialState = {
  contactModalOpen: false,
  sendContactFormInProgress: false,
  sendContactFormError: null,
  contactFormSent: false,
  contactBookings: [],
  contactBookingsRole: null,
  contactBookingsInProgress: false,
  contactBookingsError: false,
  contactListings: [],
  contactListingsInProgress: false,
  contactListingsError: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CONTACT_MODAL_OPEN: {
      return {
        ...state,
        contactModalOpen: payload,
      };
    }

    case SEND_CONTACT_FORM_REQUEST: {
      return {
        ...state,
        sendContactFormInProgress: true,
        sendContactFormError: null,
      };
    }
    // The modal deliberately stays open: it swaps the form for a confirmation
    // so the customer can see that the request went out.
    case SEND_CONTACT_FORM_SUCCESS: {
      return {
        ...state,
        sendContactFormInProgress: false,
        contactFormSent: true,
      };
    }
    case SEND_CONTACT_FORM_ERROR: {
      return {
        ...state,
        sendContactFormInProgress: false,
        sendContactFormError: payload,
      };
    }
    case RESET_CONTACT_FORM: {
      return {
        ...state,
        sendContactFormError: null,
        contactFormSent: false,
      };
    }

    case FETCH_CONTACT_BOOKINGS_REQUEST: {
      return {
        ...state,
        contactBookingsInProgress: true,
        contactBookingsError: false,
      };
    }
    case FETCH_CONTACT_BOOKINGS_SUCCESS: {
      return {
        ...state,
        contactBookingsInProgress: false,
        contactBookings: payload.bookings,
        contactBookingsRole: payload.role,
      };
    }
    case FETCH_CONTACT_BOOKINGS_ERROR: {
      return {
        ...state,
        contactBookingsInProgress: false,
        contactBookingsError: true,
        contactBookings: [],
      };
    }

    case FETCH_CONTACT_LISTINGS_REQUEST: {
      return {
        ...state,
        contactListingsInProgress: true,
        contactListingsError: false,
      };
    }
    case FETCH_CONTACT_LISTINGS_SUCCESS: {
      return {
        ...state,
        contactListingsInProgress: false,
        contactListings: payload.listings,
      };
    }
    case FETCH_CONTACT_LISTINGS_ERROR: {
      return {
        ...state,
        contactListingsInProgress: false,
        contactListingsError: true,
        contactListings: [],
      };
    }

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const openContactModal = (state) => ({
  type: CONTACT_MODAL_OPEN,
  payload: state,
});

export const sendContactFormRequest = () => ({
  type: SEND_CONTACT_FORM_REQUEST,
});
export const sendContactFormSuccess = () => ({
  type: SEND_CONTACT_FORM_SUCCESS,
});
export const sendContactFormError = (error) => ({
  type: SEND_CONTACT_FORM_ERROR,
  payload: error,
});
export const resetContactForm = () => ({
  type: RESET_CONTACT_FORM,
});

export const fetchContactBookingsRequest = () => ({ type: FETCH_CONTACT_BOOKINGS_REQUEST });
export const fetchContactBookingsSuccess = (bookings, role) => ({
  type: FETCH_CONTACT_BOOKINGS_SUCCESS,
  payload: { bookings, role },
});
export const fetchContactBookingsError = () => ({ type: FETCH_CONTACT_BOOKINGS_ERROR });

export const fetchContactListingsRequest = () => ({ type: FETCH_CONTACT_LISTINGS_REQUEST });
export const fetchContactListingsSuccess = (listings) => ({
  type: FETCH_CONTACT_LISTINGS_SUCCESS,
  payload: { listings },
});
export const fetchContactListingsError = () => ({ type: FETCH_CONTACT_LISTINGS_ERROR });

// ================ Selectors ================ //

// Three places close this modal — the dialog header, the form's cancel button
// and PortalModalContainer — and every one of them has to leave a clean slate
// behind, or the next visitor opens onto the previous confirmation. Resetting
// here rather than at each call site is the only way that stays true.
export const toggleContactModalState = (state) => (dispatch) => {
  dispatch(openContactModal(state));
  if (!state) {
    dispatch(resetContactForm());
  }
};

export const sendContactForm = (values) => async (dispatch) => {
  dispatch(sendContactFormRequest());
  try {
    await contactSupport({ formData: values });
    dispatch(sendContactFormSuccess());
    return values;
  } catch (e) {
    dispatch(sendContactFormError(e));
  }
};

const CONTACT_BOOKINGS_SIZE = 10;

// DD.MM.YYYY. The year is spelled out rather than implied: the dropdown mixes
// past and upcoming trips, and "12.08." alone cannot tell last summer's booking
// from this one.
const formatDay = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()}`;
};

const bookingStartTime = (tx) => {
  const start = tx?.booking?.attributes?.start;
  if (!start) return null;
  const time = start instanceof Date ? start.getTime() : Date.parse(start);
  return Number.isNaN(time) ? null : time;
};

// Newest trip first. Creation order is a decent proxy but not the same thing —
// a booking made today can start after one made last week. Transactions with no
// usable booking date sink to the end, keeping their relative (newest-created
// first) order thanks to a stable sort.
const byBookingStartDesc = (a, b) => {
  const aTime = bookingStartTime(a);
  const bTime = bookingStartTime(b);
  if (aTime === bTime) return 0;
  if (aTime === null) return 1;
  if (bTime === null) return -1;
  return bTime - aTime;
};

/**
 * Builds the human-readable option text for one transaction.
 *
 * The shape is `DD.MM.YYYY - DD.MM.YYYY <subject>`. A camper picks between other
 * people's listings, so his subject is the listing title — that is what tells
 * his trips apart. A host sees only his own listings on every row, so the title
 * tells him nothing and the guest does: he thinks in guests, not in inventory.
 *
 * @param {object} tx denormalised transaction
 * @param {object} options
 * @param {'camper'|'host'|'prospect'} options.role
 * @returns {string|null} the label, or null when nothing readable is left
 */
const bookingLabel = (tx, { role }) => {
  const start = formatDay(tx?.booking?.attributes?.start);
  const end = formatDay(tx?.booking?.attributes?.end);
  const period = start && end ? `${start} - ${end}` : null;
  const title = tx?.listing?.attributes?.title || null;

  // A deleted account leaves no display name behind. The row still describes a
  // real booking, so it falls back to the listing title instead of dropping.
  const guest = role === 'host' ? tx?.customer?.attributes?.profile?.displayName || null : null;
  const subject = role === 'host' ? guest || title : title;

  const parts = [period, subject].filter(Boolean);
  // The booking id is a raw UUID the customer has never seen anywhere in the
  // product, so a row with nothing readable is dropped by the caller rather
  // than offered as an id nobody can map to a trip.
  return parts.length ? parts.join(' ') : null;
};

/**
 * Loads the current user's most recent transactions for the contact form's
 * reference dropdown.
 *
 * The Marketplace API returns transactions in ASCENDING order and ignores a
 * `sort` param (same quirk InboxPage.duck.js works around), so a cheap count
 * query gives us the last page, which we then reverse.
 *
 * The last page alone only holds `totalItems % CONTACT_BOOKINGS_SIZE` items
 * when that remainder is non-zero, which would under-fill the dropdown (e.g.
 * 23 total items means page 3 holds only 3, not 10). When there is a
 * remainder we also fetch the page before it, so we always have at least
 * CONTACT_BOOKINGS_SIZE items to pick the newest from.
 *
 * The dropdown is then sorted by `booking.attributes.start` descending, not by
 * creation order. The candidate window is still the newest-created page(s) —
 * the API gives us no way to page by booking start — but within that window the
 * customer sees their most recent trip first.
 *
 * Never rejects: a failed lookup only means the form falls back to free text.
 *
 * @param {'camper'|'host'|'prospect'} role
 */
export const fetchContactBookings = (role) => async (dispatch, getState, sdk) => {
  const only = role === 'host' ? 'sale' : 'order';
  dispatch(fetchContactBookingsRequest());

  try {
    const countResponse = await sdk.transactions.query({ only, page: 1, per_page: 1 });
    const totalItems = countResponse.data.meta.totalItems || 0;
    if (totalItems === 0) {
      dispatch(fetchContactBookingsSuccess([], role));
      return;
    }

    const totalPages = Math.max(1, Math.ceil(totalItems / CONTACT_BOOKINGS_SIZE));
    const remainder = totalItems % CONTACT_BOOKINGS_SIZE;
    const pagesToFetch =
      totalPages > 1 && remainder !== 0 ? [totalPages - 1, totalPages] : [totalPages];

    // The host label names the guest, so the sale query asks for the customer
    // as well — restricted to the one profile field the label reads, the same
    // way InboxPage.duck.js does it. The order query stays as it was: a camper
    // is the customer, so there is nothing to look up.
    const isHost = role === 'host';
    const responses = await Promise.all(
      pagesToFetch.map((page) =>
        sdk.transactions.query({
          only,
          page,
          per_page: CONTACT_BOOKINGS_SIZE,
          include: isHost ? ['booking', 'listing', 'customer'] : ['booking', 'listing'],
          'fields.listing': ['title'],
          ...(isHost ? { 'fields.user': ['profile.displayName'] } : {}),
        }),
      ),
    );
    // A Marketplace API response is normalised: an entry in `data.data` holds
    // only references to its booking and listing, the resources themselves sit
    // in `data.included`. Reading `tx.booking.attributes` off `data.data`
    // yields `undefined`, so denormalise each response first — otherwise both
    // the label and the sort below have nothing to work with.
    //
    // `Promise.all` preserves the order of `pagesToFetch` regardless of which
    // request resolves first, so the concatenated list stays oldest-first.
    //
    // No `lastTransitions` filter is applied to the query above, so this also
    // returns enquiries — transactions with no booking at all. An enquiry has
    // no dates or listing-derived trip info, so `bookingLabel` can never
    // describe it and it would be dropped below anyway. Dropping it here,
    // before the slice below, means a page mixing enquiries and real bookings
    // still fills up to CONTACT_BOOKINGS_SIZE with usable entries instead of
    // coming up short by however many were enquiries. This is keyed on
    // booking-data presence, not on transition/state — a declined, cancelled
    // or expired booking still has real dates and a real listing, and
    // customers contact support about exactly those, so they must stay.
    const transactions = responses
      .flatMap((response) => denormalisedResponseEntities(response))
      .filter((tx) => tx.booking);

    const bookings = transactions
      // Reverse first so the newest-created wins any tie on the booking start.
      .reverse()
      .sort(byBookingStartDesc)
      .map((tx) => ({ id: tx.id.uuid, label: bookingLabel(tx, { role }) }))
      // Before the slice, for the same reason the enquiry filter runs early: a
      // row we cannot label must not consume one of the ten visible slots.
      .filter((booking) => booking.label)
      .slice(0, CONTACT_BOOKINGS_SIZE);

    dispatch(fetchContactBookingsSuccess(bookings, role));
  } catch (e) {
    dispatch(fetchContactBookingsError());
  }
};

// One page is the whole story in practice — a host with more listings than this
// is not picking one out of a dropdown anyway, and the free-text field stays
// reachable through the "other / not listed" entry.
const CONTACT_LISTINGS_SIZE = 50;

/**
 * Loads the current user's own listings for the contact form's reference
 * dropdown.
 *
 * `own_listings/query` returns every state the author owns — draft,
 * pendingApproval, published and closed — with no filter parameter needed,
 * which is exactly right here: a host writes to support about the listing that
 * is stuck in draft or was closed on him at least as often as about a live one.
 *
 * No sparse fieldset is requested. Per the API reference's fields.RESOURCE_TYPE
 * rule the parameter would be `fields.ownListing` (the resource type is
 * `ownListing`), but the docs carry no literal example and do not say whether a
 * wrong fields param is ignored or 400s — and a 400 here would silently sink
 * the dropdown into the free-text fallback for good. Every other
 * `ownListings.query` call site in the app passes none either, and a page of
 * listings is small, so the trim is not worth that risk untested.
 *
 * Never rejects: a failed lookup only means the form falls back to free text.
 */
export const fetchContactListings = () => async (dispatch, getState, sdk) => {
  dispatch(fetchContactListingsRequest());

  try {
    const response = await sdk.ownListings.query({ page: 1, per_page: CONTACT_LISTINGS_SIZE });
    const listings = denormalisedResponseEntities(response)
      .map((listing) => ({ id: listing.id.uuid, label: listing.attributes?.title || null }))
      // Same rule as the booking dropdown: a listing we cannot name is dropped
      // rather than offered as a raw uuid the customer has never seen. An empty
      // list is a legitimate outcome — the form falls back to free text.
      .filter((listing) => listing.label);

    dispatch(fetchContactListingsSuccess(listings));
  } catch (e) {
    dispatch(fetchContactListingsError());
  }
};
